export interface BlueConicProfile {
  getProfile(): {
    getPropertyNames?(): string[];
    /**
     * Loads the given properties for the current profile. The onSuccess function is called after the properties are loaded. The caller object is passed to the onSuccess function.
     * It is important to realize that loading the values for properties is done asynchronously. This means you can only rely on the values actually being loaded inside the onSuccess function
     * @param properties An array with the unique IDs of the profile properties that must be loaded.
     * @param caller Specifies the scope of the onSuccess function
     * @param onSucces The function to call after the values have been loaded.
     */
    loadValues?(properties: string | string[], caller?: unknown, onSucces?: () => void): void;
    getValue?(propertyName: string): string | null;
  };
}

export interface BlueConicInteraction {
  dialogueId: string;
  id: string;
  name: string;
  variantName: string;
}
export const blueConicEventNames = {
  onBeforeInteractions: 'onbeforeinteractions',
  onBeforePreListeners: 'onbeforeprelisteners',
  onEventReady: 'oneventready',
  onLifecycleStageChange: 'onlifecyclestagechange',
  onProfilePermissionChange: 'onpermissionlevelchange',
  onReady: 'onready',
  onVIEW: 'VIEW',
  onPAGEVIEW: 'PAGEVIEW',
  onSegmentChange: 'onsegmentchange',
  onUrlChange: 'onurlchange',
} as const;
type BlueConicEventNameKeys = keyof typeof blueConicEventNames;
type BlueConicEventTypes = (typeof blueConicEventNames)[BlueConicEventNameKeys];

interface BlueConicEvent {
  /**
   * Publishes a listening event. The eventContext is an optional array that can be used to pass on parameters to subscribers of the listening event.
   * @param {string} eventName The name of the event to publish.
   * @param {string[]} [eventContext] An Array containing relevant attributes. This argument is optional
   * */
  publish?(eventName: string, eventContext?: string[]): void;
  /**
   * Subscribes to an event that will be published using event.publish(eventName, eventContext)
   * @param {BlueConicEventTypes} eventName The name of the event to subscribe to.
   * @param {Object} handlerObject Specifies the scope of the handler function.
   * @param {function} handlerFunction A function that is called after the event is thrown
   */
  subscribe?(eventName: BlueConicEventTypes, handlerObject: unknown, handlerFunction): void;
  /**
   * Unsubscribes to an event
   * @param eventName The name of the event to unsubscribe to
   */
  unsubscribe?(eventName: BlueConicEventTypes): void;
}
export interface BlueConicClient {
  profile?: BlueConicProfile;
  event?: BlueConicEvent;
  getInteractions?(): BlueConicInteraction[];
  getInteractionNamesById?(interactionId: string): BlueConicInteraction | null;
}
